import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr, enUS, arSA } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

/**
 * Bannière d'avertissement affichée en haut des pages pour les utilisateurs restreints
 * À intégrer dans le layout principal
 */
const RestrictionBanner = ({ 
  isBanned,
  banType,
  banReason,
  banExpiresAt,
  restrictions = [],
  onDismiss,
  dismissible = true 
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('disputes');
  const [open, setOpen] = React.useState(true);

  if (!isBanned && restrictions.length === 0) {
    return null;
  }

  const handleDismiss = () => {
    setOpen(false);
    onDismiss?.();
  };

  const formatDate = (date) => {
    if (!date) return null;
    const localeMap = { fr, en: enUS, ar: arSA };
    const locale = localeMap[i18n.language] || fr;
    return format(new Date(date), "dd/MM/yyyy", { locale });
  };

  // Bannière de ban (priorité haute)
  if (isBanned) {
    return (
      <Collapse in={open}>
        <Alert
          severity="error"
          icon={<BlockIcon />}
          sx={{ borderRadius: 0 }}
          action={
            dismissible && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            )
          }
        >
          <AlertTitle>
            {banType === 'permanent' 
              ? t('restrictionBanner.permanentTitle') 
              : t('restrictionBanner.temporaryTitle')}
          </AlertTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Typography variant="body2">
              {banType === 'permanent' ? (
                t('restrictionBanner.permanentMessage')
              ) : (
                <>
                  {t('restrictionBanner.temporaryMessage.prefix')}
                  {banExpiresAt && (
                    <> {t('restrictionBanner.temporaryMessage.until')} <strong>{formatDate(banExpiresAt)}</strong></>
                  )}.
                </>
              )}
              {banReason ? (
                <>
                  <br />
                  <strong>{t('restrictionBanner.reasonLabel')}</strong> {banReason}
                </>
              ) : null}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => window.open('/account-restricted', '_blank')}
            >
              {t('restrictionBanner.viewDetails')}
            </Button>
          </Stack>
        </Alert>
      </Collapse>
    );
  }

  // Bannière de restrictions (moins critique)
  return (
    <Collapse in={open}>
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        sx={{ borderRadius: 0 }}
        action={
          dismissible && (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )
        }
      >
        <AlertTitle>{t('restrictionBanner.restrictionsTitle')}</AlertTitle>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="body2">
            {restrictions.length === 1 ? (
              t(`restrictionBanner.restrictionMessages.${restrictions[0].restriction_type}`)
            ) : (
              t('restrictionBanner.multipleRestrictions', { count: restrictions.length })
            )}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => navigate('/account-restricted')}
          >
            {t('restrictionBanner.viewDetails')}
          </Button>
        </Stack>
      </Alert>
    </Collapse>
  );
};

export default RestrictionBanner;
